import React, { useState, useEffect, useCallback } from 'react';
import { View, StyleSheet, FlatList, RefreshControl, Alert } from 'react-native';
import { Text, Card, Searchbar, Chip, ActivityIndicator } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import api from '../../services/api';

const CuentasCobrarScreen = () => {
  const navigation = useNavigation();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cuentas, setCuentas] = useState([]);
  const [filteredCuentas, setFilteredCuentas] = useState([]);
  const [resumen, setResumen] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [filtroActivo, setFiltroActivo] = useState('todas');

  const formatCurrency = (v) => `L. ${parseFloat(v || 0).toFixed(2)}`;

  const cargarCuentas = useCallback(async () => {
    try {
      const params = filtroActivo === 'vencidas' ? '?vencidas=1' :
                     filtroActivo !== 'todas' ? `?estado=${filtroActivo}` : '';
      const response = await api.get(`/creditos/cuentas${params}`);
      setCuentas(response.cuentas || []);
      setFilteredCuentas(response.cuentas || []);
      setResumen(response.resumen || {});
    } catch (error) {
      console.error('Error:', error);
      Alert.alert('Error', 'No se pudieron cargar las cuentas');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filtroActivo]);

  useEffect(() => {
    const unsub = navigation.addListener('focus', () => cargarCuentas());
    return unsub;
  }, [navigation, cargarCuentas]);

  useEffect(() => { cargarCuentas(); }, [cargarCuentas]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredCuentas(cuentas);
    } else {
      const q = searchQuery.toLowerCase();
      setFilteredCuentas(cuentas.filter(c =>
        (c.cliente_nombre || '').toLowerCase().includes(q) ||
        (c.nombre_negocio || '').toLowerCase().includes(q) ||
        String(c.venta_id).includes(q)
      ));
    }
  }, [searchQuery, cuentas]);

  const getEstadoStyle = (estado, diasRestantes) => {
    if (estado === 'pagada') return { bg: '#28A745', textColor: '#fff', label: 'Pagada', icon: 'checkmark-circle' };
    if (diasRestantes < 0)   return { bg: '#DC3545', textColor: '#fff', label: 'Vencida', icon: 'alert-circle' };
    if (diasRestantes <= 3)  return { bg: '#FFC107', textColor: '#000', label: 'Por vencer', icon: 'time' };
    if (estado === 'parcial') return { bg: '#17A2B8', textColor: '#fff', label: 'Parcial', icon: 'reload-circle' };
    return { bg: '#6C757D', textColor: '#fff', label: 'Pendiente', icon: 'ellipse' };
  };

  const renderCuenta = ({ item }) => {
    const est = getEstadoStyle(item.estado, item.dias_restantes);
    const estaVencida = item.dias_restantes < 0 && ['pendiente', 'parcial'].includes(item.estado);
    return (
      <Card
        style={[styles.cuentaCard, estaVencida && styles.cuentaVencida]}
        onPress={() => navigation.navigate('DetalleCuenta', { cuentaId: item.id })}
      >
        <Card.Content>
          <View style={styles.cuentaHeader}>
            <View style={{ flex: 1 }}>
              <Text style={styles.clienteNombre}>{item.cliente_nombre}</Text>
              {item.nombre_negocio ? <Text style={styles.negocioNombre}>{item.nombre_negocio}</Text> : null}
              <Text style={styles.ventaRef}>Venta #{item.venta_id}</Text>
            </View>
            <View style={{ alignItems: 'flex-end' }}>
              <Text style={styles.saldoPendiente}>{formatCurrency(item.saldo_pendiente)}</Text>
              <View style={[styles.estadoChip, { backgroundColor: est.bg }]}>
                <Ionicons name={est.icon} size={12} color={est.textColor} />
                <Text style={[styles.estadoChipText, { color: est.textColor }]}>{est.label}</Text>
              </View>
            </View>
          </View>
          <View style={styles.cuentaFooter}>
            <View style={styles.fechaRow}>
              <Ionicons name="calendar-outline" size={13} color="#888" />
              <Text style={styles.fechaLabel}>Emitida</Text>
              <Text style={styles.fechaValue}>{item.fecha_emision}</Text>
            </View>
            <View style={styles.fechaRow}>
              <Ionicons name="time-outline" size={13} color={item.dias_restantes < 0 ? '#dc3545' : '#888'} />
              <Text style={styles.fechaLabel}>Vence</Text>
              <Text style={[styles.fechaValue, item.dias_restantes < 0 && { color: '#dc3545', fontWeight: '700' }]}>
                {item.fecha_vencimiento}
              </Text>
            </View>
            <View style={[styles.diasBadge, { backgroundColor: item.dias_restantes < 0 ? '#f8d7da' : item.dias_restantes <= 3 ? '#fff3cd' : '#e9ecef' }]}>
              <Text style={[styles.diasBadgeText, { color: item.dias_restantes < 0 ? '#dc3545' : item.dias_restantes <= 3 ? '#856404' : '#666' }]}>
                {item.dias_restantes < 0 ? `${Math.abs(item.dias_restantes)}d atraso` : `${item.dias_restantes}d`}
              </Text>
            </View>
          </View>
          {item.proxima_cuota_numero && parseInt(item.total_cuotas) > 1 && item.estado !== 'pagada' && (
            <View style={styles.proximaCuotaRow}>
              <Ionicons name="cash-outline" size={13} color="#0066cc" />
              <Text style={styles.proximaCuotaText}>
                Próxima cuota {item.proxima_cuota_numero}/{item.total_cuotas}: {formatCurrency(item.proxima_cuota_saldo)} · {new Date(item.proxima_cuota_fecha).toLocaleDateString('es-HN', { day: '2-digit', month: 'short' })}
              </Text>
            </View>
          )}
          {parseFloat(item.monto_pagado) > 0 && (
            <View style={styles.progressContainer}>
              <View style={styles.progressBar}>
                <View style={[styles.progressFill, { width: `${(parseFloat(item.monto_pagado) / parseFloat(item.monto_total)) * 100}%` }]} />
              </View>
              <Text style={styles.progressText}>Pagado: {formatCurrency(item.monto_pagado)} de {formatCurrency(item.monto_total)}</Text>
            </View>
          )}
        </Card.Content>
      </Card>
    );
  };

  if (loading && !refreshing) {
    return <View style={styles.loader}><ActivityIndicator size="large" color="#0066cc" /><Text style={{ marginTop: 10, color: '#666' }}>Cargando cuentas...</Text></View>;
  }

  return (
    <View style={styles.container}>
      {/* Resumen */}
      <View style={styles.resumenRow}>
        <Card style={[styles.resumenCard, { borderLeftColor: '#dc3545' }]}>
          <Card.Content style={styles.resumenContent}>
            <Text style={[styles.resumenValue, { color: '#dc3545' }]}>{formatCurrency(resumen.total_pendiente)}</Text>
            <Text style={styles.resumenLabel}>Pendiente</Text>
          </Card.Content>
        </Card>
        <Card style={[styles.resumenCard, { borderLeftColor: '#ffc107' }]}>
          <Card.Content style={styles.resumenContent}>
            <Text style={[styles.resumenValue, { color: '#856404' }]}>{resumen.total_vencidas || 0}</Text>
            <Text style={styles.resumenLabel}>Vencidas</Text>
          </Card.Content>
        </Card>
        <Card style={[styles.resumenCard, { borderLeftColor: '#17a2b8' }]}>
          <Card.Content style={styles.resumenContent}>
            <Text style={[styles.resumenValue, { color: '#17a2b8' }]}>{resumen.total_por_vencer || 0}</Text>
            <Text style={styles.resumenLabel}>Por vencer</Text>
          </Card.Content>
        </Card>
      </View>

      {/* Filtros */}
      <View style={styles.filtros}>
        {[
          { id: 'todas', label: 'Todas' },
          { id: 'pendiente', label: 'Pendientes' },
          { id: 'parcial', label: 'Parciales' },
          { id: 'vencidas', label: 'Vencidas' },
          { id: 'pagada', label: 'Pagadas' },
        ].map(f => (
          <Chip
            key={f.id}
            selected={filtroActivo === f.id}
            showSelectedCheck={false}
            onPress={() => setFiltroActivo(f.id)}
            style={[styles.filtroChip, filtroActivo === f.id && styles.filtroActivo]}
            textStyle={filtroActivo === f.id ? styles.filtroTextoActivo : styles.filtroTextoInactivo}
          >
            {f.label}
          </Chip>
        ))}
      </View>

      <Searchbar
        placeholder="Buscar cliente, negocio, # venta..."
        onChangeText={setSearchQuery}
        value={searchQuery}
        style={styles.searchbar}
      />

      <FlatList
        data={filteredCuentas}
        keyExtractor={(item) => item.id.toString()}
        renderItem={renderCuenta}
        contentContainerStyle={styles.listContent}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); cargarCuentas(); }} />}
        ListEmptyComponent={
          <View style={styles.empty}>
            <Ionicons name="wallet-outline" size={60} color="#ccc" />
            <Text style={{ color: '#999', marginTop: 10 }}>No hay cuentas por cobrar</Text>
          </View>
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  loader: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  resumenRow: { flexDirection: 'row', padding: 8, paddingBottom: 0 },
  resumenCard: { flex: 1, marginHorizontal: 4, borderLeftWidth: 3, elevation: 2 },
  resumenContent: { alignItems: 'center', paddingVertical: 8 },
  resumenValue: { fontSize: 16, fontWeight: 'bold' },
  resumenLabel: { fontSize: 11, color: '#888', marginTop: 2 },
  filtros: { flexDirection: 'row', paddingHorizontal: 8, paddingVertical: 8, flexWrap: 'wrap' },
  filtroChip: { marginRight: 6, marginBottom: 4, backgroundColor: '#e9ecef' },
  filtroActivo: { backgroundColor: '#0066cc' },
  filtroTextoInactivo: { color: '#333' },
  filtroTextoActivo: { color: '#fff', fontWeight: '600' },
  searchbar: { marginHorizontal: 12, marginBottom: 8, elevation: 1 },
  listContent: { paddingHorizontal: 12, paddingBottom: 80 },
  cuentaCard: { marginBottom: 8, elevation: 2, borderRadius: 8 },
  cuentaVencida: { borderLeftWidth: 4, borderLeftColor: '#dc3545', backgroundColor: '#fff5f5' },
  cuentaHeader: { flexDirection: 'row', justifyContent: 'space-between' },
  clienteNombre: { fontSize: 15, fontWeight: '700', color: '#333' },
  negocioNombre: { fontSize: 13, color: '#666', fontStyle: 'italic' },
  ventaRef: { fontSize: 12, color: '#999', marginTop: 2 },
  saldoPendiente: { fontSize: 17, fontWeight: '800', color: '#dc3545' },
  estadoChip: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 12, marginTop: 4, gap: 4 },
  estadoChipText: { fontSize: 10, fontWeight: 'bold' },
  cuentaFooter: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 8, paddingTop: 8, borderTopWidth: 1, borderTopColor: '#f0f0f0', flexWrap: 'wrap', gap: 4 },
  fechaRow: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  fechaLabel: { fontSize: 11, color: '#888' },
  fechaValue: { fontSize: 11, color: '#555', fontWeight: '600' },
  diasBadge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 10 },
  diasBadgeText: { fontSize: 11, fontWeight: '700' },
  progressContainer: { marginTop: 8 },
  progressBar: { height: 6, backgroundColor: '#e9ecef', borderRadius: 3 },
  progressFill: { height: 6, backgroundColor: '#28a745', borderRadius: 3 },
  progressText: { fontSize: 11, color: '#888', marginTop: 3 },
  proximaCuotaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 8,
    paddingTop: 6,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
  },
  proximaCuotaText: { fontSize: 12, color: '#0066cc', fontWeight: '600' },
  empty: { alignItems: 'center', padding: 40 },
});

export default CuentasCobrarScreen;
